import type { FC } from 'react';
import { useState } from 'react';
import { AlertCircle, CheckCircle2, Paperclip, Send, X } from 'lucide-react';
import type { MailboxResponse, SendMessageRequest, SendMessageResponse } from '@antigravity/types';
import { apiClient, ApiClientError } from '../../services/api-client';

interface ComposeModalProps {
  mailbox: MailboxResponse;
  isOpen: boolean;
  onClose: () => void;
  onMessageSent?: (result: SendMessageResponse) => void;
}

export const ComposeModal: FC<ComposeModalProps> = ({
  mailbox,
  isOpen,
  onClose,
  onMessageSent,
}) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [textBody, setTextBody] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [mode, setMode] = useState<'text' | 'html'>('text');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SendMessageResponse | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent duplicate clicks

    setError(null);

    // Client-side validation
    const toRecipients = to
      .split(/[,;\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);

    if (toRecipients.length === 0) {
      setError('Please provide at least one recipient in the "To" field.');
      return;
    }

    const ccRecipients = cc
      .split(/[,;\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);

    const bccRecipients = bcc
      .split(/[,;\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);

    const totalRecipients = toRecipients.length + ccRecipients.length + bccRecipients.length;
    if (totalRecipients > 10) {
      setError(`Recipient count exceeds the maximum limit of 10 (specified: ${totalRecipients}).`);
      return;
    }

    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }

    if (subject.length > 998) {
      setError('Subject exceeds RFC 5322 maximum of 998 characters.');
      return;
    }

    if (!textBody.trim() && !htmlBody.trim()) {
      setError('Please provide a message body (plain text or HTML).');
      return;
    }

    const payload: SendMessageRequest = {
      from: mailbox.emailAddress,
      to: toRecipients,
      cc: ccRecipients.length > 0 ? ccRecipients : undefined,
      bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
      subject: subject.trim(),
      text: textBody.trim() || undefined,
      html: htmlBody.trim() || undefined,
    };

    setIsSubmitting(true);

    try {
      const result = await apiClient.sendMessage(mailbox.id, payload);
      setSuccess(result);
      if (onMessageSent) {
        onMessageSent(result);
      }
    } catch (err: any) {
      if (err instanceof ApiClientError) {
        setError(err.message || 'Failed to submit email. Please verify settings.');
      } else {
        setError(err.message || 'An unexpected error occurred while communicating with the mail server.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setTextBody('');
    setHtmlBody('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="compose-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn"
    >
      <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80">
          <h2 id="compose-modal-title" className="text-base font-semibold text-white flex items-center gap-2">
            <Send className="w-4 h-4 text-brand-400" />
            New Email Message
          </h2>
          <button
            type="button"
            onClick={handleResetAndClose}
            aria-label="Close compose modal"
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Banner */}
        {success ? (
          <div className="p-8 flex flex-col items-center text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Accepted for Delivery</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-md">
                Message <span className="font-mono text-brand-300">{success.messageId}</span> was accepted by the Stalwart submission service.
              </p>
              <div className="mt-3 p-3 bg-slate-800/60 rounded-xl border border-slate-700/50 text-[11px] text-slate-400 text-left space-y-1">
                <div><strong className="text-slate-300">Status:</strong> {success.status}</div>
                <div><strong className="text-slate-300">Accepted:</strong> {success.accepted.join(', ')}</div>
                <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-700/40">
                  Notice: Server acceptance indicates the message has been queued for routing. It does not constitute proof of final external delivery to remote recipient servers.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleResetAndClose}
              className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-medium text-xs transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-y-auto">
            {/* Form Fields */}
            <div className="p-6 space-y-3.5 flex-1">
              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-400" />
                  <span>{error}</span>
                </div>
              )}

              {/* Sender (Restricted) */}
              <div className="flex items-center text-xs">
                <span className="w-16 text-slate-400 font-medium">From:</span>
                <span className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800/50 text-slate-300 font-mono text-xs border border-slate-800">
                  {mailbox.displayName ? `${mailbox.displayName} <${mailbox.emailAddress}>` : mailbox.emailAddress}
                </span>
              </div>

              {/* Recipient To */}
              <div className="flex items-center text-xs">
                <label htmlFor="compose-to" className="w-16 text-slate-400 font-medium">To:</label>
                <input
                  id="compose-to"
                  type="text"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com (comma separated, max 10)"
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700/70 text-white placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                />
                {!showCcBcc && (
                  <button
                    type="button"
                    onClick={() => setShowCcBcc(true)}
                    className="ml-2 text-xs text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    Cc / Bcc
                  </button>
                )}
              </div>

              {/* Cc / Bcc Fields */}
              {showCcBcc && (
                <>
                  <div className="flex items-center text-xs">
                    <label htmlFor="compose-cc" className="w-16 text-slate-400 font-medium">Cc:</label>
                    <input
                      id="compose-cc"
                      type="text"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      placeholder="cc-recipient@example.com"
                      className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700/70 text-white placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                  <div className="flex items-center text-xs">
                    <label htmlFor="compose-bcc" className="w-16 text-slate-400 font-medium">Bcc:</label>
                    <input
                      id="compose-bcc"
                      type="text"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="bcc-recipient@example.com"
                      className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700/70 text-white placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                </>
              )}

              {/* Subject */}
              <div className="flex items-center text-xs">
                <label htmlFor="compose-subject" className="w-16 text-slate-400 font-medium">Subject:</label>
                <input
                  id="compose-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={998}
                  placeholder="Subject line"
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700/70 text-white placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                />
              </div>

              {/* Body Tabs */}
              <div className="pt-2">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMode('text')}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                        mode === 'text'
                          ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                          : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      Plain Text
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('html')}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                        mode === 'html'
                          ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                          : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      HTML Body (Optional)
                    </button>
                  </div>
                  <span className="text-[11px] text-slate-400 flex items-center gap-1">
                    <Paperclip className="w-3 h-3 text-slate-400" />
                    Attachments disabled in v0.1
                  </span>
                </div>

                {mode === 'text' ? (
                  <textarea
                    rows={8}
                    value={textBody}
                    onChange={(e) => setTextBody(e.target.value)}
                    placeholder="Write your email message here..."
                    className="w-full p-3 rounded-xl bg-slate-800/50 border border-slate-700/60 text-slate-100 placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none font-sans"
                  />
                ) : (
                  <textarea
                    rows={8}
                    value={htmlBody}
                    onChange={(e) => setHtmlBody(e.target.value)}
                    placeholder="<p>HTML formatted email body...</p>"
                    className="w-full p-3 rounded-xl bg-slate-800/50 border border-slate-700/60 text-emerald-300 placeholder-slate-400 text-xs focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none font-mono"
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 border-t border-slate-800 bg-slate-900/80 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                Single-message API (max 10 recipients, 2MB size limit)
              </span>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 text-xs font-medium transition-colors"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold shadow-lg shadow-brand-500/20 transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Send Email</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
